import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../services/auth.service';
import { ChatbotService, ChatResponse } from '../../services/chatbot.service';
import { jwtDecode } from 'jwt-decode';
import { AppConstants } from '../../../shared/utils/app-constants';

export interface ChatMessage {
  id: string;
  sender: 'bot' | 'user';
  text: string;
  timestamp: Date;
  isWelcome?: boolean;
  showFeedback?: boolean;
  feedbackSubmitted?: boolean;
  selectedFeedback?: 'up' | 'down';
  feedbackTimer?: any;
}

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss']
})
export class ChatbotComponent implements OnInit, OnDestroy {
  @ViewChild('closeConfirmDialog') closeConfirmDialog: TemplateRef<any>;
  @ViewChild('messagesScrollContainer') messagesScrollContainer: ElementRef;

  isOpen = false;
  isMinimized = false;
  hasActiveSession = false;
  messages: ChatMessage[] = [];
  inputMessage = '';
  isBotTyping = false;
  private conversationId: string | null = null;

  private userSubscription: Subscription;
  private langChangeSubscription: Subscription;
  private dialogRef: MatDialogRef<any> | null = null;

  constructor(
    public authService: AuthService,
    private matDialog: MatDialog,
    private chatbotService: ChatbotService,
    private sanitizer: DomSanitizer,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.userSubscription = this.authService.userUpdated.subscribe(() => {
      if (!this.authService.isLoggedIn()) {
        this.clearSession();
      } else {
        this.updateWelcomeMessage();
      }
    });

    this.langChangeSubscription = this.translateService.onLangChange.subscribe(() => {
      this.updateWelcomeMessage();
    });
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    this.clearSession();
  }

  get userFirstName(): string {
    if (this.authService.user?.First_Name) {
      return this.authService.user.First_Name.trim();
    }

    try {
      const tokenResponseStr = localStorage.getItem(AppConstants.CURRENT_USER_TOKEN);
      if (tokenResponseStr) {
        const tokenResponse = JSON.parse(tokenResponseStr);
        if (tokenResponse?.access_token) {
          const decoded: any = jwtDecode(tokenResponse.access_token);
          if (decoded?.given_name) {
            return decoded.given_name.trim();
          }
          if (decoded?.name) {
            return decoded.name.split(' ')[0].trim();
          }
        }
      }
    } catch (e) {
      // Ignored
    }

    return '';
  }

  toggleChatbot(): void {
    if (!this.isOpen) {
      this.openChatbot();
    } else if (this.isMinimized) {
      this.restoreChatbot();
    } else {
      this.minimizeChatbot();
    }
  }

  openChatbot(): void {
    this.isOpen = true;
    this.isMinimized = false;

    if (!this.hasActiveSession) {
      this.startNewSession();
    } else {
      this.scrollToBottom();
    }
  }

  minimizeChatbot(): void {
    this.isMinimized = true;
  }

  restoreChatbot(): void {
    this.isMinimized = false;
    this.isOpen = true;
    this.scrollToBottom();
  }

  startNewSession(): void {
    this.hasActiveSession = true;
    this.messages = [];

    this.messages.push({
      id: this.generateId(),
      sender: 'bot',
      text: this.getWelcomeText(),
      timestamp: new Date(),
      isWelcome: true
    });

    this.scrollToBottom();
  }

  private getWelcomeText(): string {
    const firstName = this.userFirstName;
    const greetingHeader = firstName
      ? this.translateService.instant('chatbot.welcome.greeting', { name: firstName })
      : this.translateService.instant('chatbot.welcome.greetingNoName');
    const welcomeSubtitle = this.translateService.instant('chatbot.welcome.subtitle');
    return `${greetingHeader}\n${welcomeSubtitle}`;
  }

  private updateWelcomeMessage(): void {
    const welcomeMsg = this.messages.find(m => m.isWelcome);
    if (welcomeMsg) {
      welcomeMsg.text = this.getWelcomeText();
    }
  }

  onCloseClick(): void {
    this.dialogRef = this.matDialog.open(this.closeConfirmDialog, {
      maxWidth: '25vw',
      height: ' 180px',
      panelClass: 'chatbot-close-dialog-panel',
      autoFocus: false,
      disableClose: true
    });

    this.dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      this.dialogRef = null;
      if (confirmed) {
        this.clearSession();
        this.isOpen = false;
        this.isMinimized = false;
      }
    });
  }

  cancelClose(): void {
    if (this.dialogRef) {
      this.dialogRef.close(false);
    }
  }

  confirmClose(): void {
    if (this.dialogRef) {
      this.dialogRef.close(true);
    }
  }

  clearSession(): void {
    // Cancel all pending feedback timers per AC 8
    this.messages.forEach(msg => {
      if (msg.feedbackTimer) {
        clearTimeout(msg.feedbackTimer);
        msg.feedbackTimer = null;
      }
    });

    this.hasActiveSession = false;
    this.messages = [];
    this.inputMessage = '';
    this.isBotTyping = false;
    this.conversationId = null;
  }

  sendMessage(): void {
    const text = this.inputMessage?.trim();
    if (!text) {
      return;
    }

    this.messages.push({
      id: this.generateId(),
      sender: 'user',
      text,
      timestamp: new Date()
    });

    this.inputMessage = '';
    this.scrollToBottom();

    this.isBotTyping = true;
    this.chatbotService.sendMessage(text, this.conversationId).subscribe({
      next: (response: ChatResponse) => {
        this.isBotTyping = false;
        if (response?.conversationId) {
          this.conversationId = response.conversationId;
        }
        this.addBotMessage(response?.reply || this.translateService.instant('chatbot.messages.emptyResponse'));
      },
      error: (err) => {
        console.error('Chatbot API error:', err);
        this.isBotTyping = false;
        this.addBotMessage(this.translateService.instant('chatbot.messages.networkError'));
      }
    });
  }

  private addBotMessage(text: string): void {
    const botMsg: ChatMessage = {
      id: this.generateId(),
      sender: 'bot',
      text,
      timestamp: new Date(),
      showFeedback: false,
      feedbackSubmitted: false
    };

    // 1-minute (60,000 ms) delay for feedback per Story 599 AC 5
    botMsg.feedbackTimer = setTimeout(() => {
      if (this.hasActiveSession) {
        botMsg.showFeedback = true;
        this.cdr.detectChanges();
        this.scrollToBottom();
      }
    }, 60000);

    this.messages.push(botMsg);
    this.scrollToBottom();
  }

  submitFeedback(msg: ChatMessage, option: 'up' | 'down'): void {
    if (msg.feedbackSubmitted) {
      return;
    }
    msg.feedbackSubmitted = true;
    msg.selectedFeedback = option;
    this.cdr.detectChanges();
    this.scrollToBottom();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.messagesScrollContainer) {
        try {
          const element = this.messagesScrollContainer.nativeElement;
          element.scrollTop = element.scrollHeight;
        } catch (err) {
          // Ignored
        }
      }
    }, 50);
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  formatMessage(text: string): SafeHtml {
    if (!text) {
      return '';
    }

    // Escape HTML characters to prevent XSS
    let html = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Headings: ### Title -> <div class="chat-h3">Title</div>
    html = html.replace(/^### (.*$)/gim, '<div class="chat-h3">$1</div>');
    html = html.replace(/^## (.*$)/gim, '<div class="chat-h2">$1</div>');
    html = html.replace(/^# (.*$)/gim, '<div class="chat-h1">$1</div>');

    // Bold: **text** -> <strong>$1</strong>
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Italic: *text* -> <em>$1</em>
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');

    // Bullet points: lines starting with * or -
    const lines = html.split('\n');
    const processedLines: string[] = [];
    let inList = false;

    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
        if (!inList) {
          inList = true;
          processedLines.push('<ul class="chat-bullet-list">');
        }
        processedLines.push(`<li>${trimmed.substring(2)}</li>`);
      } else {
        if (inList) {
          inList = false;
          processedLines.push('</ul>');
        }
        processedLines.push(line);
      }
    }
    if (inList) {
      processedLines.push('</ul>');
    }

    html = processedLines.join('\n');

    // Cleanly handle paragraph breaks vs single line breaks
    html = html.replace(/(?:\r\n|\r|\n){2,}/g, '<div class="chat-para-break"></div>');
    html = html.replace(/(?:\r\n|\r|\n)/g, '<br/>');
    html = html.replace(/<\/ul><br\/>/g, '</ul>');
    html = html.replace(/<ul class="chat-bullet-list"><br\/>/g, '<ul class="chat-bullet-list">');
    html = html.replace(/<\/li><br\/>/g, '</li>');
    html = html.replace(/<div class="chat-para-break"><\/div><br\/>/g, '<div class="chat-para-break"></div>');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
}
